// src/hooks/useResponsive.ts
import { useState, useEffect, useCallback } from 'react';

// Стандартные брейкпоинты в соответствии с Tailwind CSS
export const breakpoints = {
  xs: 0,       // Сверхмалые экраны (маленькие мобильные)
  sm: 640,     // Малые экраны (мобильные)
  md: 768,     // Средние экраны (большие мобильные, маленькие планшеты)
  lg: 1024,    // Большие экраны (планшеты, нетбуки)
  xl: 1280,    // Очень большие экраны (ноутбуки, маленькие десктопы)
  '2xl': 1536  // Огромные экраны (десктопы)
};

// Типы устройств
export type DeviceType = 'mobile' | 'tablet' | 'desktop';

// Типы ориентации
export type Orientation = 'portrait' | 'landscape';

// Интерфейс возвращаемого объекта
export interface ResponsiveInfo {
  // Размеры экрана
  width: number;
  height: number;
  
  // Тип устройства
  deviceType: DeviceType;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
  
  // Ориентация устройства
  orientation: Orientation;
  isPortrait: boolean;
  isLandscape: boolean;
  
  // Проверка брейкпоинтов
  breakpoint: keyof typeof breakpoints;
  isBreakpoint: (breakpoint: keyof typeof breakpoints, operator?: 'up' | 'down' | 'only') => boolean;
}

/**
 * Объединенный хук для работы с адаптивностью, заменяющий useDeviceType и useOrientation
 * Предоставляет информацию о размере экрана, типе устройства и ориентации
 * Использует ResizeObserver для лучшей производительности
 */
export function useResponsive(): ResponsiveInfo {
  // Проверяем доступность window для SSR
  const isClient = typeof window !== 'undefined';
  
  // Состояния для размеров экрана
  const [dimensions, setDimensions] = useState({
    width: isClient ? (window as any).innerWidth || 0 : 0,
    height: isClient ? (window as any).innerHeight || 0 : 0
  });
  
  // Определяем текущий брейкпоинт
  const getCurrentBreakpoint = useCallback((width: number): keyof typeof breakpoints => {
    if (width >= breakpoints['2xl']) return '2xl';
    if (width >= breakpoints.xl) return 'xl';
    if (width >= breakpoints.lg) return 'lg';
    if (width >= breakpoints.md) return 'md';
    if (width >= breakpoints.sm) return 'sm';
    return 'xs';
  }, []);
  
  // Определяем тип устройства более гибко
  const getDeviceType = useCallback((width: number): DeviceType => {
    // Можно настроить границы в соответствии с требованиями проекта
    if (width >= breakpoints.lg) return 'desktop';
    if (width >= breakpoints.sm) return 'tablet';
    return 'mobile';
  }, []);
  
  // Определяем ориентацию
  const getOrientation = useCallback((width: number, height: number): Orientation => {
    return width > height ? 'landscape' : 'portrait';
  }, []);
  
  // Эффект для отслеживания изменений размеров экрана
  useEffect(() => {
    if (!isClient) return;
    
    // Функция обновления размеров
    const updateDimensions = () => {
      setDimensions({
        width: (window as any).innerWidth || 0,
        height: (window as any).innerHeight || 0
      });
    };
    
    // Создаем ResizeObserver для лучшей производительности
    let resizeObserver: ResizeObserver | null = null;
    
    try {
      // Используем ResizeObserver, если он доступен
      resizeObserver = new ResizeObserver(() => {
        // Достаточно для реагирования на изменения размера окна
        (window as any).requestAnimationFrame(() => {
          updateDimensions();
        });
      });
      
      // Отслеживаем изменения размера body
      resizeObserver.observe(document.body);
    } catch (e) {
      // Если ResizeObserver не поддерживается, используем традиционный подход
      console.warn('ResizeObserver not supported, falling back to event listener');
      (window as any).addEventListener('resize', updateDimensions);
    }
    
    // Также слушаем событие ориентации для мобильных устройств
    (window as any).addEventListener('orientationchange', updateDimensions);
    
    // Вызываем функцию обновления для инициализации
    updateDimensions();
    
    // Очистка при размонтировании
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      } else {
        (window as any).removeEventListener('resize', updateDimensions);
      }
      (window as any).removeEventListener('orientationchange', updateDimensions);
    };
  }, [isClient]);
  
  // Получаем актуальные значения
  const { width, height } = dimensions;
  const breakpoint = getCurrentBreakpoint(width);
  const deviceType = getDeviceType(width);
  const orientation = getOrientation(width, height);
  
  // Функция для проверки брейкпоинтов с операторами сравнения
  const isBreakpoint = useCallback((
    checkBreakpoint: keyof typeof breakpoints, 
    operator: 'up' | 'down' | 'only' = 'only'
  ): boolean => {
    const breakpointValue = breakpoints[checkBreakpoint];
    const keys = Object.keys(breakpoints) as Array<keyof typeof breakpoints>;
    const checkIndex = keys.indexOf(checkBreakpoint);
    
    switch (operator) {
      case 'up':
        // Проверка "от и выше" (md и больше)
        return width >= breakpointValue;
      case 'down':
        // Проверка "от и ниже" (md и меньше)
        return width < breakpoints[keys[checkIndex + 1] || '2xl'] || checkBreakpoint === '2xl';
      case 'only':
      default:
        // Проверка "только данный брейкпоинт"
        return breakpoint === checkBreakpoint;
    }
  }, [width, breakpoint]);
  
  // Возвращаем полную информацию об адаптивности
  return {
    // Размеры экрана
    width,
    height,
    
    // Тип устройства
    deviceType,
    isMobile: deviceType === 'mobile',
    isTablet: deviceType === 'tablet',
    isDesktop: deviceType === 'desktop',
    
    // Ориентация
    orientation,
    isPortrait: orientation === 'portrait',
    isLandscape: orientation === 'landscape',
    
    // Брейкпоинты
    breakpoint,
    isBreakpoint
  };
}

export default useResponsive;